import { useState } from 'react';
import { Plus, X, Sparkles, Loader2, AlertTriangle, Info, ChevronDown, CheckCircle2 } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { refineItem } from '../lib/generationClient';

interface CardConfig {
  id: string;
  title: string;
  listKey: string;
  instruction?: string;
  maxEntries?: number;
}

interface Section2CardsProps {
  darkMode: boolean;
  cards: CardConfig[];
  cardTexts: Record<string, { aiDraft: string; finalText: string; showAiDraft: boolean; saved: boolean }>;
  onUpdateCardText: (cardId: string, field: 'aiDraft' | 'finalText' | 'showAiDraft' | 'saved', value: any) => void;
  onGenerateCard: (cardId: string) => void;
  entries: Record<string, Array<{ text: string }>>;
  onEntriesChange: (listKey: string, entries: Array<{ text: string }>) => void;
  onSuggestCard?: (cardId: string) => void;
  suggestLoadingCardId?: string | null;
  collapsedCards?: Record<string, boolean>;
  onToggleCard?: (id: string) => void;
  onCardComplete?: (cardId: string) => void;
  isCardComplete?: (cardId: string) => boolean;
}

export function Section2Cards({
  darkMode,
  cards,
  cardTexts,
  onUpdateCardText,
  onGenerateCard,
  entries,
  onEntriesChange,
  onSuggestCard,
  suggestLoadingCardId,
  collapsedCards: collapsedCardsProp,
  onToggleCard,
  onCardComplete,
  isCardComplete,
}: Section2CardsProps) {

  const placeholders: Record<string, string> = {
    istZustand: 'Beschreiben Sie einen Aspekt des Ist-Zustands...',
    staerken: 'Beschreiben Sie eine Stärke...',
    schwaechen: 'Beschreiben Sie eine Schwäche...',
  };

  const [collapsedCardsLocal, setCollapsedCardsLocal] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    cards.forEach((card) => { initial[card.id] = true; });
    return initial;
  });
  const collapsedCards = collapsedCardsProp ?? collapsedCardsLocal;
  const toggleCard = (id: string) => {
    if (onToggleCard) onToggleCard(id);
    else setCollapsedCardsLocal(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {cards.map((card) => {
        const cardState = cardTexts[card.id] || { aiDraft: '', finalText: '', showAiDraft: false, saved: false };
        const listEntries = entries[card.listKey] || [{ text: '' }];
        const placeholder = placeholders[card.listKey] || 'Beschreiben Sie einen Punkt...';
        const isCollapsed = !!collapsedCards[card.id];

        return (
          <div
            key={card.id}
            data-card-id={card.id}
            className={`rounded-lg border ${
              darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
            } shadow-sm scroll-mt-[70px]`}
            onBlur={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node)) return;
              const r = e.relatedTarget as HTMLElement | null;
              if (r && (r.closest('[data-card-id]') || r.closest('[data-section-id]'))) return;
              // relatedTarget is null for many in-card mouse interactions
              // (e.g. a plain <button> click on WebKit/macOS doesn't move
              // keyboard focus there). Re-check activeElement a tick later
              // instead of trusting the synchronous relatedTarget.
              const container = e.currentTarget;
              setTimeout(() => {
                if (container.contains(document.activeElement)) return;
                onCardComplete?.(card.id);
              }, 0);
            }}
          >
            {/* Card Header — clickable */}
            <div
              className={`flex items-center justify-between px-6 md:px-8 py-4 cursor-pointer select-none transition-colors rounded-lg ${
                isCollapsed ? '' : 'rounded-b-none'
              } ${darkMode ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50'}`}
              onClick={() => toggleCard(card.id)}
            >
              <h3 className={`text-base md:text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {card.title}
              </h3>
              <div className="flex items-center gap-2 shrink-0 ml-3">
                {isCollapsed && isCardComplete?.(card.id) && (
                  <span
                    title="Inhalt vorhanden"
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-medium ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}
                  >
                    <CheckCircle2 className="w-3 h-3" />
                    Inhalt
                  </span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''} ${darkMode ? 'text-slate-400' : 'text-slate-400'}`} />
              </div>
            </div>

            {/* Collapsible card content */}
            <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'}`}>
            <div className="overflow-hidden">
            <div className="px-6 md:px-8 pb-6 md:pb-8 pt-2">
            {card.instruction && (
              <div className={`flex items-start gap-2.5 mb-6 px-4 py-2.5 rounded-xl border text-sm ${
                darkMode
                  ? 'bg-blue-900/20 border-blue-800 text-blue-300'
                  : 'bg-blue-50 border-blue-200 text-blue-800'
              }`}>
                <span className="mt-0.5">💡</span>
                <p className="leading-relaxed">{card.instruction}</p>
              </div>
            )}

            {/* Two-Column Layout */}
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              {/* LEFT: Textarea-Liste */}
              <div className="flex-1 lg:w-[50%]">
                <div className={`rounded-lg p-5 ${
                  darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50/50 border-slate-200'
                } border`}>
                  {/* Vorschlaege generieren Button */}
                  {onSuggestCard && (
                    <button
                      onClick={() => onSuggestCard(card.id)}
                      disabled={suggestLoadingCardId === card.id}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 mb-4 rounded-lg font-medium text-sm transition-colors border-2 border-dashed disabled:opacity-50 ${
                        darkMode
                          ? 'border-blue-500/50 text-blue-400 hover:bg-blue-900/20 hover:border-blue-400'
                          : 'border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500'
                      }`}
                    >
                      <span className="inline-flex">
                        {suggestLoadingCardId === card.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      </span>
                      <span>{suggestLoadingCardId === card.id ? 'Wird generiert...' : 'Vorschläge generieren'}</span>
                    </button>
                  )}

                  <div className="space-y-3">
                    {/* Warnbanner bei zu vielen Einträgen */}
                    {card.maxEntries && listEntries.filter(e => e.text.trim()).length > card.maxEntries && (
                      <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${
                        darkMode ? 'bg-amber-900/20 border-amber-700 text-amber-300' : 'bg-amber-50 border-amber-300 text-amber-800'
                      }`}>
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>Für den Bericht werden nur {card.maxEntries} Stichpunkte verwendet. Bitte entfernen Sie {listEntries.filter(e => e.text.trim()).length - card.maxEntries} überschüssige{listEntries.filter(e => e.text.trim()).length - card.maxEntries === 1 ? 'n Punkt' : ' Punkte'}.</span>
                      </div>
                    )}
                    {listEntries.map((entry, index) => {
                      const filledCount = listEntries.filter(e => e.text.trim()).length;
                      const isOverLimit = card.maxEntries && entry.text.trim() && index >= card.maxEntries && filledCount > card.maxEntries;
                      return (
                      <div key={index} className={`flex items-start gap-3 ${isOverLimit ? 'pl-2 border-l-4 border-amber-400 rounded-sm' : ''}`}>
                        <textarea
                          translate="no"
                          value={entry.text}
                          ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                          onChange={(e) => {
                            const updated = [...listEntries];
                            updated[index] = { text: e.target.value };
                            onEntriesChange(card.listKey, updated);
                          }}
                          onInput={(e) => { const el = e.currentTarget; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }}
                          placeholder={placeholder}
                          rows={2}
                          className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-hidden ${
                            darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'border-slate-300'
                          }`}
                        />
                        <button
                          onClick={() => {
                            if (listEntries.length > 1) {
                              onEntriesChange(card.listKey, listEntries.filter((_, i) => i !== index));
                            }
                          }}
                          disabled={listEntries.length === 1}
                          className={
                            listEntries.length === 1
                              ? darkMode
                                ? 'p-2.5 rounded-lg border border-slate-600 text-slate-500 cursor-not-allowed transition-colors'
                                : 'p-2.5 rounded-lg border border-slate-200 text-slate-300 cursor-not-allowed transition-colors'
                              : darkMode
                                ? 'p-2.5 rounded-lg border border-slate-600 text-slate-400 hover:bg-red-900/30 hover:border-red-500 hover:text-red-400 transition-colors'
                                : 'p-2.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors'
                          }
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      );
                    })}
                    {/* "Weiteren Punkt hinzufügen" nur wenn unter Limit */}
                    {(!card.maxEntries || listEntries.filter(e => e.text.trim()).length < card.maxEntries) && (
                    <button
                      onClick={() => onEntriesChange(card.listKey, [...listEntries, { text: '' }])}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed transition-colors text-sm font-medium ${
                        darkMode
                          ? 'border-slate-600 text-slate-400 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-900/20'
                          : 'border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <Plus className="w-4 h-4" />
                      Weiteren Punkt hinzufügen
                    </button>
                    )}
                  </div>

                  {/* Text generieren Button */}
                  <div className={`mt-5 pt-4 border-t ${darkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                    <button
                      onClick={() => onGenerateCard(card.id)}
                      className={`w-full px-4 py-2 rounded-lg border transition-colors text-sm font-medium shadow-sm ${
                        darkMode ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      ✨ Text generieren
                    </button>
                  </div>
                </div>
              </div>

              {/* RIGHT: RichTextEditor */}
              <div className="flex-1 lg:w-[50%] flex flex-col">
                <div className={`rounded-lg flex-1 flex flex-col ${
                  darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50/50 border-slate-200'
                } border`}>
                  <div className="mx-5 my-5 flex-1 flex flex-col">
                    <RichTextEditor
                      content={cardState.finalText}
                      onChange={(html) => onUpdateCardText(card.id, 'finalText', html)}
                      darkMode={darkMode}
                      minHeight="200px"
                      onRefineItem={async (itemHtml, instruction, mode) => {
                        const result = await refineItem({ itemHtml, instruction: instruction || undefined, mode });
                        if (!result.ok) throw new Error(result.error.message);
                        return result.data.text;
                      }}
                    />
                  </div>

                </div>
              </div>
            </div>
            </div>{/* pb wrapper */}
            </div>{/* overflow-hidden */}
            </div>{/* grid collapse */}
          </div>
        );
      })}
    </div>
  );
}
